(() => {
  'use strict';

  const wishlistKey = 'tizico-shopify-wishlist-v1';
  const productStates = new WeakMap();
  let wishlist = [];
  let wishlistOpener = null;

  function shopRoot() {
    const configured = window.Shopify?.routes?.root || document.documentElement.dataset.routesRoot || document.documentElement.dataset.shopRoot || document.body.dataset.routesRoot || '/';
    try {
      const url = new URL(configured, window.location.origin);
      if (url.origin !== window.location.origin) return '/';
      return `${url.pathname.replace(/\/$/, '')}/`;
    } catch { return '/'; }
  }

  const route = path => `${shopRoot()}${path.replace(/^\//, '')}`;

  function ui(key, values = {}) {
    const message = window.Tizico?.strings?.[key];
    if (typeof message !== 'string') return '';
    return message.replace(/__([A-Z]+)__/g, (token, name) => String(values[name.toLowerCase()] ?? token));
  }

  function parseJSON(element, fallback = null) {
    try { return element ? JSON.parse(element.textContent) : fallback; } catch { return fallback; }
  }

  function safeURL(value, sameOrigin = false) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol) || (sameOrigin && url.origin !== window.location.origin)) return '';
      return sameOrigin ? `${url.pathname}${url.search}${url.hash}` : url.href;
    } catch { return ''; }
  }

  function sanitizeFavorite(value) {
    if (!value || typeof value !== 'object' || !/^[a-z0-9][a-z0-9-]{0,254}$/i.test(value.handle || '')) return null;
    const url = safeURL(value.url || route(`products/${value.handle}`), true);
    if (!url || typeof value.title !== 'string' || !value.title.trim()) return null;
    let price = typeof value.price === 'string' ? value.price.slice(0, 100) : '';
    if (typeof value.price === 'number' && Number.isFinite(value.price)) {
      const currency = window.Shopify?.currency?.active || document.documentElement.dataset.currency || 'USD';
      try { price = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(value.price / 100); } catch { price = ''; }
    }
    return { handle: value.handle, url, title: value.title.trim().slice(0, 300), image: safeURL(value.image || ''), price };
  }

  function readWishlist() {
    try {
      const stored = JSON.parse(localStorage.getItem(wishlistKey));
      if (!Array.isArray(stored)) return;
      const seen = new Set();
      wishlist = stored.slice(0, 100).map(sanitizeFavorite).filter(item => {
        if (!item || seen.has(item.handle)) return false;
        seen.add(item.handle);
        return true;
      });
    } catch { wishlist = []; }
  }

  function saveWishlist() {
    try { localStorage.setItem(wishlistKey, JSON.stringify(wishlist)); } catch {}
  }

  function favoriteFromButton(button) {
    try { return sanitizeFavorite(JSON.parse(button.getAttribute('data-wishlist'))); } catch { return null; }
  }

  function updateWishlistButtons() {
    document.querySelectorAll('[data-wishlist]').forEach(button => {
      const favorite = favoriteFromButton(button);
      if (!favorite) return;
      const saved = wishlist.some(item => item.handle === favorite.handle);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', ui(saved ? 'wishlist_remove' : 'wishlist_save', { title: favorite.title }));
    });
    const count = document.getElementById('wishlist-count');
    if (count) { count.textContent = String(wishlist.length); count.hidden = wishlist.length === 0; }
  }

  function renderWishlist() {
    const content = document.getElementById('wishlist-content');
    if (!content) return;
    content.replaceChildren();
    if (!wishlist.length) {
      const empty = document.createElement('div');
      empty.className = 'wishlist-empty empty-state';
      const heading = document.createElement('h3');
      heading.textContent = ui('wishlist_empty');
      const description = document.createElement('p');
      description.textContent = ui('wishlist_empty_description');
      const link = document.createElement('a');
      link.className = 'primary-button';
      link.href = route('collections/all');
      link.textContent = ui('shop_gadgets');
      empty.append(heading, description, link);
      content.append(empty);
      return;
    }
    const note = document.createElement('p');
    note.className = 'wishlist-note';
    note.textContent = ui('wishlist_note');
    const list = document.createElement('div');
    list.className = 'wishlist-grid';
    wishlist.forEach(item => {
      const card = document.createElement('article');
      card.className = 'wishlist-item';
      const imageLink = document.createElement('a');
      imageLink.className = 'wishlist-item-image';
      imageLink.href = item.url;
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        image.width = 240;
        image.height = 240;
        imageLink.append(image);
      }
      const copy = document.createElement('div');
      copy.className = 'wishlist-item-copy';
      const heading = document.createElement('h3');
      const link = document.createElement('a');
      link.href = item.url;
      link.textContent = item.title;
      heading.append(link);
      copy.append(heading);
      if (item.price) { const price = document.createElement('p'); price.className = 'price'; price.textContent = item.price; copy.append(price); }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'text-link wishlist-remove';
      remove.dataset.wishlistRemove = item.handle;
      remove.textContent = ui('remove');
      remove.setAttribute('aria-label', ui('wishlist_remove', { title: item.title }));
      copy.append(remove);
      card.append(imageLink, copy);
      list.append(card);
    });
    content.append(note, list);
  }

  function openWishlist(opener) {
    const dialog = document.getElementById('wishlist-dialog');
    if (!dialog) return;
    wishlistOpener = opener || document.activeElement;
    renderWishlist();
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    document.body.classList.add('dialog-open');
  }

  function closeWishlist() {
    const dialog = document.getElementById('wishlist-dialog');
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else { dialog.removeAttribute('open'); finishWishlistClose(); }
  }

  function finishWishlistClose() {
    document.body.classList.remove('dialog-open');
    if (wishlistOpener?.isConnected) wishlistOpener.focus();
  }

  function toggleWishlist(button) {
    const favorite = favoriteFromButton(button);
    if (!favorite) return;
    const saved = wishlist.some(item => item.handle === favorite.handle);
    wishlist = saved ? wishlist.filter(item => item.handle !== favorite.handle) : [...wishlist.filter(item => item.handle !== favorite.handle), favorite].slice(-100);
    saveWishlist();
    updateWishlistButtons();
    const status = document.getElementById('wishlist-status');
    if (status) status.textContent = ui(saved ? 'wishlist_removed' : 'wishlist_saved');
  }

  function setFormStatus(form, message, isError = false, includeCartLink = false) {
    let status = form.querySelector('[data-product-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'form-status product-form-status';
      status.dataset.productStatus = '';
      form.append(status);
    }
    status.setAttribute('role', isError ? 'alert' : 'status');
    status.setAttribute('aria-live', isError ? 'assertive' : 'polite');
    status.classList.toggle('form-error', isError);
    status.replaceChildren(document.createTextNode(message));
    if (includeCartLink) {
      const link = document.createElement('a');
      link.href = route('cart');
      link.textContent = ui('view_bag');
      status.append(document.createTextNode(' '), link);
    }
  }

  async function refreshCartCount() {
    const response = await fetch(route('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('cart_refresh_failed');
    const cart = await response.json();
    if (!Number.isInteger(cart.item_count) || cart.item_count < 0) return;
    const count = document.getElementById('cart-count');
    if (count) { count.textContent = String(cart.item_count); count.hidden = cart.item_count === 0; }
    const link = document.getElementById('cart-open');
    if (link) link.setAttribute('aria-label', ui('bag_label', { count: cart.item_count }));
    document.dispatchEvent(new CustomEvent('tizico:cart-updated', { detail: { cart } }));
  }

  async function addProduct(form) {
    if (form.dataset.submitting === 'true') return;
    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) { setFormStatus(form, ui('choose_available'), true); return; }
    const buttons = [...form.querySelectorAll('[type="submit"]')];
    const previouslyDisabled = buttons.map(button => button.disabled);
    form.dataset.submitting = 'true';
    form.setAttribute('aria-busy', 'true');
    buttons.forEach(button => { button.disabled = true; });
    setFormStatus(form, ui('adding'));
    try {
      const response = await fetch(route('cart/add.js'), {
        method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: formData,
      });
      const result = await response.json();
      if (!response.ok) {
        const detail = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : ui('add_error');
        setFormStatus(form, detail, true, true);
        try { await refreshCartCount(); } catch {}
        return;
      }
      setFormStatus(form, ui('added'), false, true);
      try { await refreshCartCount(); } catch {}
      document.dispatchEvent(new CustomEvent('tizico:cart-added'));
    } catch {
      setFormStatus(form, ui('unconfirmed_add'), true, true);
    } finally {
      delete form.dataset.submitting;
      form.removeAttribute('aria-busy');
      buttons.forEach((button, index) => { button.disabled = previouslyDisabled[index]; });
    }
  }

  function showMedia(section, id) {
    const panels = [...section.querySelectorAll('[data-media-id]')];
    if (!panels.some(panel => panel.dataset.mediaId === String(id))) return;
    panels.forEach(panel => {
      const selected = panel.dataset.mediaId === String(id);
      panel.hidden = !selected;
      if (!selected) panel.querySelectorAll('video').forEach(video => video.pause());
    });
    section.querySelectorAll('[data-gallery-select]').forEach(button => {
      const selected = button.dataset.gallerySelect === String(id);
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-active', selected);
    });
  }

  function updateVariant(section, updateURL = false) {
    const state = productStates.get(section);
    const select = section.querySelector('[data-variant-select]');
    if (!state || !select) return;
    const variant = state.product.variants.find(item => String(item.id) === select.value);
    const submit = section.querySelector('[data-product-submit]');
    const label = section.querySelector('[data-add-label]');
    if (!variant) { if (submit) submit.disabled = true; if (label) label.textContent = ui('unavailable'); return; }
    const money = state.money[String(variant.id)];
    const price = section.querySelector('[data-product-price]');
    const compare = section.querySelector('[data-compare-price]');
    if (price && money) price.textContent = money.price;
    if (compare) { compare.hidden = !(variant.compare_at_price > variant.price); if (money) compare.textContent = money.compare; }
    if (submit) submit.disabled = !variant.available;
    if (label) label.textContent = ui(variant.available ? 'add_to_bag' : 'sold_out');
    section.dispatchEvent(new CustomEvent('tizico:variant-changed', { bubbles: true, detail: { variant, money } }));
    const sku = section.querySelector('[data-product-sku]');
    if (sku) { sku.textContent = variant.sku || ''; const wrapper = section.querySelector('[data-sku-wrapper]'); if (wrapper) wrapper.hidden = !variant.sku; }
    const quantity = section.querySelector('[data-product-quantity]');
    if (quantity && money) {
      quantity.min = String(money.minimum || 1);
      quantity.step = String(money.increment || 1);
      if (money.maximum != null) quantity.max = String(money.maximum); else quantity.removeAttribute('max');
      quantity.value = String(money.minimum || 1);
    }
    const imageMedia = state.product.media?.find(media => media.preview_image?.id === variant.featured_image?.id);
    const mediaId = money?.mediaId || variant.featured_media?.id || imageMedia?.id || variant.featured_image?.id;
    if (mediaId) showMedia(section, mediaId);
    const status = section.querySelector('[data-product-status]');
    if (status && updateURL) status.replaceChildren();
    if (updateURL && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      url.searchParams.delete('option_values');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }

  function initProducts(scope = document) {
    const sections = [...scope.querySelectorAll('[data-tizico-product]')];
    if (scope.matches?.('[data-tizico-product]')) sections.unshift(scope);
    sections.forEach(section => {
      if (productStates.has(section)) return;
      const product = parseJSON(section.querySelector('[data-product-json]'));
      if (!product || !Array.isArray(product.variants)) return;
      productStates.set(section, { product, money: parseJSON(section.querySelector('[data-variant-money]'), {}) || {} });
      const thumbnails = section.querySelector('[data-gallery-thumbnails]');
      if (thumbnails) thumbnails.hidden = false;
      section.querySelectorAll('[data-quantity-change]').forEach(button => { button.hidden = false; });
      showMedia(section, section.dataset.galleryStart || section.querySelector('[data-media-id]')?.dataset.mediaId);
      updateVariant(section);
    });
  }

  function setMenu(open, restoreFocus = false) {
    const menu = document.getElementById('mobile-nav');
    const toggle = document.getElementById('menu-toggle');
    if (!menu || !toggle) return;
    menu.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    if (restoreFocus) toggle.focus();
  }

  function init() {
    readWishlist();
    updateWishlistButtons();
    initProducts();
    const dialog = document.getElementById('wishlist-dialog');
    if (dialog) {
      dialog.addEventListener('close', finishWishlistClose);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeWishlist();
      });
    }
    document.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-tizico-product-form], [data-tizico-quick-add]') || !window.fetch || !window.FormData) return;
      if (event.submitter && event.submitter.name !== 'add') return;
      event.preventDefault();
      if (form.reportValidity()) addProduct(form);
    });
    document.addEventListener('change', event => {
      if (!(event.target instanceof Element) || !event.target.matches('[data-variant-select]')) return;
      const section = event.target.closest('[data-tizico-product]');
      if (section) updateVariant(section, true);
    });
    document.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button, a');
      if (!button) return;
      if (button.matches('#menu-toggle')) {
        event.preventDefault(); setMenu(document.getElementById('mobile-nav')?.hidden !== false);
      } else if (button.matches('#wishlist-open, [data-wishlist-open]')) {
        event.preventDefault(); openWishlist(button);
      } else if (button.matches('[data-wishlist-close], #wishlist-dialog [data-close-dialog]')) {
        event.preventDefault(); closeWishlist();
      } else if (button.matches('[data-wishlist]')) {
        event.preventDefault(); toggleWishlist(button);
      } else if (button.matches('[data-wishlist-remove]')) {
        event.preventDefault(); wishlist = wishlist.filter(item => item.handle !== button.dataset.wishlistRemove); saveWishlist(); updateWishlistButtons(); renderWishlist(); document.querySelector('#wishlist-content .wishlist-remove, #wishlist-content a')?.focus();
      } else if (button.matches('[data-gallery-select]')) {
        event.preventDefault(); const section = button.closest('[data-tizico-product]'); if (section) showMedia(section, button.dataset.gallerySelect);
      } else if (button.matches('[data-quantity-change]')) {
        const form = button.closest('form');
        const input = form?.querySelector('[data-product-quantity]');
        if (input) { if (Number(button.dataset.quantityChange) > 0) input.stepUp(); else input.stepDown(); input.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      if (button.closest('#mobile-nav') && button.tagName === 'A') setMenu(false);
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && document.getElementById('mobile-nav')?.hidden === false) setMenu(false, true);
    });
    document.addEventListener('shopify:section:load', event => { initProducts(event.target); updateWishlistButtons(); });
    window.addEventListener('pageshow', event => { if (event.persisted) refreshCartCount().catch(() => {}); });
    window.addEventListener('storage', event => { if (event.key === wishlistKey) { readWishlist(); updateWishlistButtons(); if (document.getElementById('wishlist-dialog')?.open) renderWishlist(); } });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
